<template>
  <div class="product-information">
    <basic-accordion
      v-if="productInfoAvailable"
      id="details"
      :title="$string('product.information.title')"
      :active="getActiveState('details')"
      block-class="product-information"
      @toggle-accordion="toggleAccordion"
      @click="productInfoAccordionDataLayer($string('product.information.title'))"
    >
      <template #closeIcon>
        <icon-close />
      </template>

      <template #content>
        <div
          v-if="productInfo.keyFeatures"
          class="product-information__content-block"
        >
          <ul>
            <li
              v-for="(item, index) in productInfo.keyFeatures"
              :key="`key-feature-${index}`"
              class="product-information__list-item"
              v-html="item.value"
            />
          </ul>
        </div>

        <div
          v-if="variant.included"
          class="product-information__content-block"
        >
          <h4
            class="product-information__subtitle"
            v-text="$string('product.information.included')"
          />

          <p
            v-for="(item, index) in variant.included"
            :key="`included-${index}`"
            v-text="item"
          />
        </div>

        <div
          v-else-if="productInfo.included"
          class="product-information__content-block"
        >
          <h4
            class="product-information__subtitle"
            v-text="$string('product.information.included')"
          />

          <p
            v-for="(item, index) in productInfo.included"
            :key="`included-${index}`"
            v-text="item.value"
          />
        </div>

        <div
          v-if="productInfo.origin"
          class="product-information__content-block"
        >
          <h4
            class="product-information__subtitle"
            v-text="$string('product.information.origin')"
          />

          <p v-text="productInfo.origin || productInfo.origin" />
        </div>

        <div
          v-if="productInfo.sku"
          class="product-information__content-block"
        >
          <span class="text-body-xs">
            {{ $string('product.information.sku') }}:{{ productInfo.sku }}
          </span>
        </div>
      </template>
    </basic-accordion>

    <basic-accordion
      v-if="variant.ingredients"
      id="ingredients"
      :active="getActiveState('ingredients')"
      :title="$string('product.information.ingredients')"
      block-class="product-information"
      @toggle-accordion="toggleAccordion"
      @click="productInfoAccordionDataLayer($string('product.information.ingredients'))"
    >
      <template #closeIcon>
        <icon-close />
      </template>

      <template #content>
        <div class="product-information__content-block">
          <p
            v-html="variant.ingredients"
          />
        </div>
      </template>
    </basic-accordion>

    <basic-accordion
      v-else-if="productInfo.ingredients"
      id="ingredients"
      :active="getActiveState('ingredients')"
      :title="$string('product.information.ingredients')"
      block-class="product-information"
      @toggle-accordion="toggleAccordion"
    >
      <template #closeIcon>
        <icon-close />
      </template>

      <template #content>
        <div class="product-information__content-block">
          <p
            v-html="productInfo.ingredients"
          />
        </div>
      </template>
    </basic-accordion>

    <basic-accordion
      v-if="productInfo.directions || productInfo.video"
      id="information"
      :active="getActiveState('information')"
      :title="$string('product.information.use')"
      block-class="product-information"
      @toggle-accordion="toggleAccordion"
      @click="productInfoAccordionDataLayer($string('product.information.use'))"
    >
      <template #closeIcon>
        <icon-close />
      </template>

      <template #content>
        <div
          v-if="productInfo.video"
          class="product-information__video-container"
        >
          <video-element
            :srcset="productInfo.video.sources"
            :controls="true"
            :loop="false"
            :autoplay="false"
            :title="productInfo.video.alt"
            :poster="productInfo.video.preview_image.src"
            :muted="false"
          />
        </div>

        <div
          v-if="productInfo.directions"
          class="
            product-information__content-block
            product-information__directions
          "
        >
          <p
            v-for="(item, index) in productInfo.directions"
            :key="`directions-${index}`"
            v-html="item.value"
          />
        </div>
      </template>
    </basic-accordion>

    <basic-accordion
      v-if="productInfo.refundsContent"
      id="refunds"
      :active="getActiveState('refunds')"
      :title="$string('product.information.refunds')"
      block-class="product-information"
      @toggle-accordion="toggleAccordion"
      @click="productInfoAccordionDataLayer($string('product.information.refunds'))"
    >
      <template #closeIcon>
        <icon-close />
      </template>

      <template #content>
        <div
          class="
            product-information__content-block
            product-information__content-block--rte
          "
          v-html="productInfo.refundsContent"
        />
      </template>
    </basic-accordion>
  </div>
</template>

<script>

/**
 * Vue: Product information (product-information)
 * -----------------------------------------------------------------------------
 * Product information accordions with metafield/page content.
 *
 * @param {Object} productInfo - Product info from liquid.
 *
 */
import BasicAccordion from '~async/basic-accordion/basic-accordion'
import VideoElement from '~async/video-element/video-element'
import IconClose from '~icons/misc/close.svg'

export default {
  name: 'ProductInformation',

  components: {
    BasicAccordion,
    VideoElement,
    IconClose,
  },

  props: {
    productInfo: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      accordions: [
        {
          id: 'details',
          active: true,
        },
        {
          id: 'ingredients',
          active: false,
        },
        {
          id: 'information',
          active: false,
        },
        {
          id: 'refunds',
          active: false,
        },
      ],
    }
  },

  computed: {

    /**
     * Computes if there is any product info data available.
     * @returns {Boolean}
     */
    productInfoAvailable() {
      return (
        this.productInfo.keyFeatures ||
        this.variant.included ||
        this.productInfo.origin ||
        this.productInfo.sku
      )
    },
  },

  methods: {

    /**
     * Toggle accordion's active state.
     * @param {String} id - Id of accordion.
     */
    toggleAccordion(id) {
      const accordion = this.accordions.find(item => item.id === id)

      if (accordion.active) {
        accordion.active = false
        return
      }

      this.accordions.forEach((accordion) => {
        if (accordion.id === id) {
          accordion.active = true
          return
        }

        accordion.active = false
      })
    },

    /**
     * Get accordion's active state.
     * @param {String} id - Id of accordion.
     */
    getActiveState(id) {
      const accordion = this.accordions.find(item => item.id === id)

      return accordion.active
    },

    /**
     * Send the product info accordion click event info to the data layer.
     * @param {string} label - the clicked accordion title.
     */
    productInfoAccordionDataLayer(label) {
      cnvs.EventBus.emit('Datalayer:productInfoAccordion', label.toLowerCase())
    },

  },
}
</script>

<style lang="scss">
@import './product-information';
</style>
